import { ReactNode, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { BigNumber } from 'ethers';
import { Skeleton, Table, Text, AssetInfo } from '@cronos-labs/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';
import { isMobile } from '@deficonnect/browser-utils';

import getAssetLogoUri from '@components/SupportedAssetIcon/getAssetLogoUri';
import {
  formatTotalUnderlyingAmount,
  formatTotalUnderlyingUsdValue,
} from '@lib/units';
import useSupplyApy from '@hooks/useSupplyApy';
import useBorrowApy from '@hooks/useBorrowApy';
import { useGetUserBoostCollections } from '@components/XTonicBoost/XTonicBoostingHooks';
import { BoostType } from '@hooks/useGetXTonicBoostDailyDistributeRate';

import NetApyCell from './NetApyCell';
import AssetUtilization from './AssetUtilization';
import { Mode } from './types';

interface TableBodyCellProps {
  align?: 'right' | 'center' | 'left';
  children: ReactNode;
  className?: string;
}

function TableBodyCell({
  align,
  children,
  className,
}: TableBodyCellProps): JSX.Element {
  return (
    <Table.BodyCell
      align={align}
      as="div"
      className={clsx('table-cell align-middle', className)}
      role="cell"
    >
      {children}
    </Table.BodyCell>
  );
}

interface MarketsTableRowProps {
  asset: TectonicAsset;
  mode: Mode;
  supplyAmount: Null<BigNumber>;
  supplyInUsd: Null<BigNumber>;
  borrowAmount: Null<BigNumber>;
  borrowInUsd: Null<BigNumber>;
  updateNetBorrowApy: (symbol: string, netApy: number) => void;
  updateNetSupplyApy: (symbol: string, netApy: number) => void;
}

function MarketsTableRow({
  asset,
  mode,
  supplyAmount,
  supplyInUsd,
  borrowAmount,
  borrowInUsd,
  updateNetBorrowApy,
  updateNetSupplyApy,
}: MarketsTableRowProps): JSX.Element {
  const href = `/markets/${mode.toLowerCase()}/${asset.symbol.toLowerCase()}`;

  const {
    netSupplyApy,
    supplyApy,
    tonicSupplyApy,
    partnerTokenSupplyApy,
    netPartnerTokenSupplyApy,
    isLoading: isSupplyLoading,
  } = useSupplyApy(mode, asset);
  const {
    netBorrowApy,
    borrowApy,
    tonicBorrowApy,
    partnerTokenBorrowApy,
    netPartnerTokenBorrowApy,
    isLoading: isBorrowLoading,
  } = useBorrowApy(mode, asset);

  useEffect(() => {
    if (netSupplyApy && !isSupplyLoading) {
      updateNetSupplyApy(asset.symbol, netSupplyApy);
    }
  }, [netSupplyApy, asset.symbol, updateNetSupplyApy, isSupplyLoading, mode]);

  useEffect(() => {
    if (netBorrowApy && !isBorrowLoading) {
      updateNetBorrowApy(asset.symbol, netBorrowApy);
    }
  }, [netBorrowApy, asset.symbol, updateNetBorrowApy, isBorrowLoading, mode]);

  const isMobileView = isMobile();

  const { data: boostedSupplyCollections } = useGetUserBoostCollections(
    BoostType.SUPPLY
  );
  const { data: boostedBorrowCollections } = useGetUserBoostCollections(
    BoostType.BORROW
  );
  const boostedCollections = useMemo(() => {
    if (boostedSupplyCollections && boostedBorrowCollections) {
      return [...boostedSupplyCollections, ...boostedBorrowCollections];
    }
    return [];
  }, [boostedSupplyCollections, boostedBorrowCollections]);

  return (
    <Link href={href} passHref legacyBehavior>
      <Table.BodyRow
        as="a"
        className="table-row"
        role="row"
        data-testid="tableRow"
      >
        <TableBodyCell>
          <div className="flex">
            <AssetInfo
              logoUrl={getAssetLogoUri(asset.symbol) ?? ''}
              symbol={asset.symbol}
              name={asset.name}
              isBoosted={boostedCollections.some(
                (item) => item === asset.tTokenAddress
              )}
            />
          </div>
        </TableBodyCell>
        <TableBodyCell align="right" className="mobile:hidden">
          <Text>
            {supplyAmount ? (
              formatTotalUnderlyingAmount(asset, supplyAmount)
            ) : (
              <Skeleton />
            )}
          </Text>
          <Text className="text-small text-onSurface60">
            {supplyInUsd ? (
              formatTotalUnderlyingUsdValue(asset, supplyInUsd)
            ) : (
              <Skeleton />
            )}
          </Text>
        </TableBodyCell>
        <TableBodyCell align="right" className="mobile:hidden">
          <Text>
            {borrowAmount ? (
              formatTotalUnderlyingAmount(asset, borrowAmount)
            ) : (
              <Skeleton />
            )}
          </Text>
          <Text className="text-small text-onSurface60">
            {borrowInUsd ? (
              formatTotalUnderlyingUsdValue(asset, borrowInUsd)
            ) : (
              <Skeleton />
            )}
          </Text>
        </TableBodyCell>
        <TableBodyCell align="center" className="tablet:hidden mobile:hidden">
          <div className="flex justify-end">
            <div className="w-[100px]">
              <AssetUtilization
                borrowAmount={borrowAmount}
                supplyAmount={supplyAmount}
              />
            </div>
          </div>
        </TableBodyCell>
        <TableBodyCell align="right">
          <NetApyCell
            className="items-start gap-[2px] desktop:items-end tablet:items-end"
            assetType="Supply"
            netApy={netSupplyApy}
            isLoading={isSupplyLoading}
            apy={supplyApy}
            tonicApy={tonicSupplyApy}
            partnerTokenApy={partnerTokenSupplyApy}
            netPartnerTokenApy={netPartnerTokenSupplyApy}
            skeletonWidth={isMobileView ? 'small' : 'large'}
          />
        </TableBodyCell>
        <TableBodyCell align="right" className="last:pr-3">
          <NetApyCell
            className="items-start gap-[2px] desktop:items-end tablet:items-end"
            assetType="Borrow"
            netApy={netBorrowApy}
            isLoading={isBorrowLoading}
            apy={borrowApy}
            tonicApy={tonicBorrowApy}
            partnerTokenApy={partnerTokenBorrowApy}
            netPartnerTokenApy={netPartnerTokenBorrowApy}
            skeletonWidth={isMobileView ? 'small' : 'large'}
          />
        </TableBodyCell>
      </Table.BodyRow>
    </Link>
  );
}

export default MarketsTableRow;
